export const ALLOWED_VIDEO_EXTENSIONS = ["mp4", "mkv", "avi", "webm", "mov"];
export const ALLOWED_DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx"];

export function getFileExtension(docPath) {
  if (!docPath) return "";

  const path = String(docPath).split("?")[0];
  const parts = path.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

export function getExtensionFromDocType(docType) {
  if (!docType) return "";

  const normalized = String(docType).toLowerCase();
  if (normalized === "docx") return "docx";
  if (normalized === "doc") return "doc";
  if (normalized === "pdf") return "pdf";
  if (normalized === "document") return "docx";

  return "";
}

export function isStoredDocument(docPath, docType = "") {
  const normalized = String(docType || "").toLowerCase();
  if (["pdf", "doc", "docx", "document"].includes(normalized)) return true;

  const ext = getFileExtension(docPath);
  return ALLOWED_DOCUMENT_EXTENSIONS.includes(ext);
}

export function stripDocumentExtension(path) {
  if (!path) return "";

  const [pathPart, ...queryParts] = String(path).split("?");
  const query = queryParts.length ? `?${queryParts.join("?")}` : "";
  return pathPart.replace(/\.(pdf|docx?)$/i, "") + query;
}

export function resolveDocumentPath(docPath, docType = "") {
  if (!docPath) return "";

  const path = docPath.trim();

  if (isStoredDocument(path, docType)) {
    return stripDocumentExtension(path);
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return path.replace(/^\/+/, "");
}

export function isDocumentDocType(docType) {
  if (!docType) return false;

  const normalized = String(docType).toLowerCase();
  return ["pdf", "doc", "docx", "document"].includes(normalized);
}

export function isTopicPreviewable(docPath, docType) {
  if (!docPath) return false;
  if (docType === "video") return true;
  if (isDocumentDocType(docType)) return true;

  return ALLOWED_DOCUMENT_EXTENSIONS.includes(getFileExtension(docPath));
}

export function getDocumentKind(docPath, docType = "") {
  const pathExt = getFileExtension(docPath);
  if (pathExt === "pdf") return "pdf";
  if (pathExt === "doc" || pathExt === "docx") return "word";

  const normalized = String(docType || "").toLowerCase();
  if (normalized === "docx" || normalized === "doc" || normalized === "document") {
    return "word";
  }

  return "pdf";
}

export function getVideoMimeType(docPath) {
  const ext = getFileExtension(docPath);
  const mimeMap = {
    mp4: "video/mp4",
    mkv: "video/x-matroska",
    avi: "video/x-msvideo",
    webm: "video/webm",
    mov: "video/quicktime",
  };

  return mimeMap[ext] || "video/mp4";
}

export function isAllowedVideoFile(file) {
  if (!file) return false;

  const ext = file.name.split(".").pop().toLowerCase();
  return (
    ALLOWED_VIDEO_EXTENSIONS.includes(ext) || (file.type || "").includes("video")
  );
}

export function isAllowedDocumentFile(file) {
  if (!file) return false;

  const ext = file.name.split(".").pop().toLowerCase();
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  return (
    ALLOWED_DOCUMENT_EXTENSIONS.includes(ext) ||
    allowedTypes.includes(file.type)
  );
}

export function getImageBaseUrl(imageURL) {
  const base = (imageURL || process.env.VUE_APP_IMAGE_URL || "").trim();
  return base.replace(/\/$/, "");
}

export function buildMediaUrl(docPath, imageURL) {
  if (!docPath) return "";
  if (/^https?:\/\//i.test(docPath)) {
    return docPath;
  }

  const base = getImageBaseUrl(imageURL);
  if (!base) return "";

  const path = docPath.replace(/^\/+/, "");
  return `${base}/${encodeURI(path)}`;
}

export function buildWordPreviewUrl(docPath, imageURL, docType = "") {
  const fileUrl = buildMediaUrl(resolveDocumentPath(docPath, docType), imageURL);
  if (!fileUrl) return "";

  return `https://docs.google.com/gview?url=${encodeURIComponent(
    fileUrl
  )}&embedded=true`;
}

export function buildDocumentPreview(docPath, imageURL, docType = "") {
  const resolvedPath = resolveDocumentPath(docPath, docType);
  const fileUrl = buildMediaUrl(resolvedPath, imageURL);
  const kind = getDocumentKind(docPath, docType);

  if (!fileUrl) {
    return {
      kind: "",
      previewUrl: "",
      downloadUrl: "",
    };
  }

  if (kind === "word") {
    return {
      kind: "word",
      previewUrl: buildWordPreviewUrl(docPath, imageURL, docType),
      downloadUrl: fileUrl,
    };
  }

  return {
    kind: "pdf",
    previewUrl: `${fileUrl}#toolbar=0`,
    downloadUrl: fileUrl,
  };
}

export function buildPdfPreviewUrl(docPath, imageURL, docType = "") {
  return buildDocumentPreview(docPath, imageURL, docType).previewUrl;
}

export function buildTopicFileUrl(docPath, imageURL, docType = "") {
  return buildMediaUrl(resolveDocumentPath(docPath, docType), imageURL);
}

// -----------------------------------------------------------------------------
// Previous media URL logic (kept for reference)
// -----------------------------------------------------------------------------
// export function normalizePdfPath(docPath) {
//   if (!docPath) return "";
//
//   let path = docPath.trim();
//   if (/^https?:\/\//i.test(path)) {
//     return path;
//   }
//
//   path = path.replace(/^\/+/, "");
//   if (/\.pdf$/i.test(path)) {
//     return path;
//   }
//   if (/\.(docx?)$/i.test(path)) {
//     return path.replace(/\.(docx?)$/i, ".pdf");
//   }
//
//   return `${path}.pdf`;
// }
//
// export function buildPdfPreviewUrl(docPath, imageURL) {
//   const pdfPath = normalizePdfPath(docPath);
//   const url = buildMediaUrl(pdfPath, imageURL);
//   return url ? `${url}#toolbar=0` : "";
// }
//
// export function buildTopicFileUrl(docPath, imageURL, docType) {
//   if (docType === "pdf") {
//     return buildMediaUrl(normalizePdfPath(docPath), imageURL);
//   }
//
//   return buildMediaUrl(docPath, imageURL);
// }
